import { AccountAllocationGrid } from '@components/AccountAllocationGrid';
import type { AccountAllocationGridProp } from '@components/AccountAllocationGrid/types';
import { datadogLogs } from '@datadog/browser-logs';
import type { TranslatedString } from '@interfaces/shared';
import CancelWorkFlowDialog from '@ovComponents/3-pattern/cancelWorkFlowDialog/cancelWorkFlowDialog';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { Button, Typography, useToast } from 'hephaestus/web';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  HouseholdModelPortfolioDraftSkeleton,
  HouseholdModelPortfolioDraftUnavailable,
} from '../householdModelPortfolioDraft/householdModelPortfolioDraftStates';
import { unpackStagedAccountAllocations } from './accountAllocationStaging';
import { useEditAccountAllocations } from './hooks/useEditAccountAllocations';
import { useStagedAccountAllocations } from './hooks/useStagedAccountAllocations';

const TEST_ID = 'edit-account-allocations';

// The workflow provider retries the context save, but its promise never
// settles once every retry fails, so Continue gives up waiting after this.
export const STAGING_TIMEOUT_MS = 15_000;

const settlesWithin = (pending: Promise<void> | void, ms: number) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Staging did not finish within ${ms}ms`)),
      ms,
    );
    Promise.resolve(pending).then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

type EditAccountAllocationsOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  enableCancel?: boolean;
  cancelButton?: TranslatedString;
};

export type EditAccountAllocationsProp = {
  options: EditAccountAllocationsOptions;
  clientGroupId?: string;
  onNext: (branchId?: string) => void | Promise<void>;
  stepLoading?: boolean;
  workflowCompletionId?: string;
  onWorkflowCancelled?: () => void;
};

export const EditAccountAllocations = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
  workflowCompletionId,
  onWorkflowCancelled,
}: EditAccountAllocationsProp) => {
  const { translateBackendString } = useLocalization();
  const { workflowData } = useContext(WorkflowContext);
  const { t } = useTranslation('workflowCompletions');
  const { enqueueToast } = useToast();
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  const { report, loading, error, refetch } = useEditAccountAllocations(
    householdClientGroupId,
  );

  useEffect(() => {
    if (!error) return;
    datadogLogs.logger.error(
      'Edit account allocations load failed',
      { clientGroupId: householdClientGroupId },
      error,
    );
  }, [error, householdClientGroupId]);

  const { state: stagedState, stage } = useStagedAccountAllocations({
    householdClientGroupId,
    report,
  });
  // Only a staging for this household's current report comes back into the
  // grid; one for an earlier report would put edits on moved holdings.
  const staged =
    stagedState.status === 'STAGED' ? stagedState.staged : undefined;
  const stagedDraft = useMemo(
    () => staged && unpackStagedAccountAllocations(staged),
    [staged],
  );

  // Fails closed: the step only advances once the matrix is staged, so the
  // signed IPS can never apply an allocation the workflow didn't record.
  // A save that throws, rejects or never finishes ends in the same toast.
  const stageAndContinue: AccountAllocationGridProp['onSave'] = async ({
    matrix,
    lockedCells,
    uploadedCells,
  }) => {
    if (!householdClientGroupId || !report) {
      datadogLogs.logger.error('Edit account allocations staging skipped', {
        clientGroupId: householdClientGroupId,
      });
      return;
    }
    try {
      await settlesWithin(
        stage({ matrix, lockedCells, uploadedCells }),
        STAGING_TIMEOUT_MS,
      );
    } catch (stageError: unknown) {
      datadogLogs.logger.error(
        'Edit account allocations staging failed',
        { clientGroupId: householdClientGroupId },
        stageError instanceof Error ? stageError : undefined,
      );
      enqueueToast(t('editAccountAllocations.stagingFailed'), {
        variant: 'error',
      });
      return;
    }
    // Awaited so Continue stays disabled until the workflow has advanced.
    await onNext();
  };

  const retry = () => {
    refetch().catch((retryError: unknown) => {
      datadogLogs.logger.error(
        'Edit account allocations retry failed',
        { clientGroupId: householdClientGroupId },
        retryError instanceof Error ? retryError : undefined,
      );
    });
  };

  const title = translateBackendString(options?.title);

  if (loading || stepLoading) {
    return (
      <HouseholdModelPortfolioDraftSkeleton title={title} testId={TEST_ID} />
    );
  }

  if (error || !report) {
    return (
      <HouseholdModelPortfolioDraftUnavailable
        title={title}
        message={t('editAccountAllocations.unavailable')}
        testId={TEST_ID}
        onRetry={error ? retry : undefined}
      />
    );
  }

  return (
    <>
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {title}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      <AccountAllocationGrid
        report={report}
        onSave={stageAndContinue}
        initialDraft={stagedDraft}
        footerActions={
          options?.enableCancel &&
          workflowCompletionId !== undefined && (
            <>
              <Button
                type='button'
                variant='contained'
                color='secondary'
                sx={{ mt: 3, mr: 2 }}
                onClick={() => setConfirmingCancel(true)}
              >
                {translateBackendString(options.cancelButton) ||
                  t('shared:cancel')}
              </Button>
              <CancelWorkFlowDialog
                worflowId={workflowCompletionId}
                open={confirmingCancel}
                setCloseDialog={setConfirmingCancel}
                onComplete={onWorkflowCancelled}
              />
            </>
          )
        }
      />
    </>
  );
};
